import { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import CustomInput from "../../common/customInput/customInput";
import CustomButton from "../../common/customButton/customButton";
import CustomCheckbox from "../../common/customCheckbox/customCheckbox";
import { getProperty } from "../../utils/apiResponseHelper";
import UniformLoader from "../../common/uniformLoader/uniformLoader";
import { trackFreeTrialSignupStep } from "../../utils/analytics";
import "./freeDownloadsRegister.css";

const MEMBERS_LOGIN_URL = "https://www.ksol.co.uk/contents/members-login";

const FreeDownloadsRegister = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [parentName, setParentName] = useState("");
  const [childYearGroup, setChildYearGroup] = useState("");
  const [emailExists, setEmailExists] = useState(false);
  const [emailChecked, setEmailChecked] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [showStickyForm, setShowStickyForm] = useState(false);
  const [mentorAccount, setMentorAccount] = useState(false);
  const [fdlVerified, setFDLVerified] = useState(false);
  const [fdlNotVerified, setFDLNotVerified] = useState(false);
  const [accountExists, setAccountExists] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [ipAddress, setIpAddress] = useState("");
  const [registering, setRegistering] = useState(false);
  const [trialExistsMessage, setTrialExistsMessage] = useState(false);
  const [existingAccountMessage, setExistingAccountMessage] = useState("");

  const heroFormRef = useRef(null);

  const defaultBaseUrl =
    process.env.NODE_ENV === "production"
      ? "https://ksol.co.uk"
      : "https://192.168.168.140";

  const prefix = process.env.REACT_APP_BASE_URL || defaultBaseUrl;

  const openMembersLoginInNewWindow = () => {
    try {
      const newWindow = window.open(
        MEMBERS_LOGIN_URL,
        "_blank",
        "noopener,noreferrer",
      );
      if (newWindow) {
        newWindow.opener = null;
      }
    } catch (error) {
      // Ignore popup/open errors and notify parent frame below.
    }

    if (window.parent !== window.self) {
      try {
        window.parent.postMessage(
          { type: "openNewWindow", url: MEMBERS_LOGIN_URL },
          "*",
        );
      } catch (e) {
        // Ignore postMessage errors.
      }
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setEmailChecked(false);
    setEmailExists(false);
    setEmailError("");
    setMentorAccount(false);
    setFDLVerified(false);
    setFDLNotVerified(false);
    setAccountExists(false);
    setTrialExistsMessage(false);
    setExistingAccountMessage("");
  };

  const handleMarketingConsentChange = (e) => {
    setMarketingConsent(e.target.checked);
  };

  const getIpAddress = async () => {
    try {
      const response = await fetch("https://api.ipify.org?format=json");
      const data = await response.json();
      return data.ip;
    } catch (error) {
      return "";
    }
  };

  useEffect(() => {
    const fetchIp = async () => {
      const ip = await getIpAddress();
      setIpAddress(ip);
    };
    fetchIp();
  }, []);

  const checkEmailExists = async () => {
    if (!email || !email.trim()) {
      setEmailError("Please enter an email address");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setEmailError("Please enter a valid email address");
      return;
    }

    setCheckingEmail(true);
    setEmailError("");
    setTrialExistsMessage(false);
    setExistingAccountMessage("");

    try {
      const response = await fetch(
        `${prefix}/api-40/api40/Registration/CheckEmailStatus?Email=${encodeURIComponent(email.trim())}`,
        {
          headers: {
            "X-API-Key": "8dnd6%3258%&k+_FsaTK25SCBNE58*t9",
          },
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Try to parse as JSON first, fallback to text
      let data;
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        data = await response.json();
      } else {
        const textData = await response.text();
        // Try to parse as JSON if it looks like JSON
        try {
          data = JSON.parse(textData);
        } catch {
          // If it's a plain string response, wrap it in an object
          data = { status: textData };
        }
      }

      const trimmedEmail = email.trim();

      // Get status with fallback for case sensitivity
      const status =
        getProperty(data[0] || data, "status") ||
        data[0]?.Status ||
        data[0]?.status ||
        data?.status ||
        data?.Status;

      if (status === "Email_Is_Mentor" || status === "Email_Is_Mentor") {
        sessionStorage.removeItem("pendingLoginEmail");
        sessionStorage.setItem("loginRequiresUsername", "true");

        let hasExistingTrial = false;
        try {
          const prospectResponse = await fetch(
            `${prefix}/api-40/api40/Registration/GetStudentProspect?Email=${encodeURIComponent(trimmedEmail)}`,
            {
              headers: {
                "X-API-Key": "8dnd6%3258%&k+_FsaTK25SCBNE58*t9",
              },
            },
          );

          if (prospectResponse.ok) {
            const prospectData = await prospectResponse.json();
            const prospectMessage =
              getProperty(prospectData, "message") ||
              prospectData?.Message ||
              prospectData?.message;

            if (prospectMessage === "Students on a trial") {
              hasExistingTrial = true;
            }
          }
        } catch (prospectError) {}

        if (hasExistingTrial) {
          setExistingAccountMessage(
            "This email has already been used for a free trial. Please log in with your KSOL username and password, or call us on 0121 733 6558.",
          );
          setTrialExistsMessage(true);
          setCheckingEmail(false);
          return;
        }

        setMentorAccount(true);
        setExistingAccountMessage(
          "Please log in with your KSOL username and password, or call us on 0121 733 6558.",
        );
        setTrialExistsMessage(true);
        setCheckingEmail(false);
        return; // Exit early to prevent showing form fields
      } else if (status === "FDL_Verified" || status === "FDL_Verified") {
        trackFreeTrialSignupStep("email_entry", 1);
        trackFreeTrialSignupStep("email_verification", 3);
        setFDLVerified(true);
        // Store email in localStorage for full account signup later
        localStorage.setItem("email", trimmedEmail);
        sessionStorage.setItem("pendingLoginEmail", trimmedEmail);
        // Navigate to parent registration to continue full account setup
        navigate("/parentRegistration");
        // Also notify parent if in iframe
        if (window.parent !== window.self) {
          try {
            window.parent.postMessage(
              { type: "navigate", path: "/parentRegistration" },
              "*",
            );
          } catch (e) {
            // Ignore postMessage errors, navigation already happened
          }
        }
        return; // Exit early to prevent showing form fields
      } else if (
        status === "FDL_Not_Verified" ||
        status === "FDL_Not_Verified"
      ) {
        trackFreeTrialSignupStep("email_entry", 1);
        setFDLNotVerified(true);
        // Store email in sessionStorage for verification screen
        sessionStorage.setItem("pendingLoginEmail", trimmedEmail);
        sessionStorage.setItem("isFreeDownloadsRegistration", "true");
        // Also store in localStorage for persistence (for full account signup later)
        localStorage.setItem("email", trimmedEmail);
        // Send verification email (don't await to avoid delay)
        fetch(
          `${prefix}/api-40/api40/Registration/EmailVerifcationCode?Email=${encodeURIComponent(trimmedEmail)}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-API-Key": "8dnd6%3258%&k+_FsaTK25SCBNE58*t9",
            },
          },
        ).catch((verifyError) => {});
        // Navigate immediately to verification screen - don't wait for email
        navigate("/verification");
        // Also notify parent if in iframe
        if (window.parent !== window.self) {
          try {
            window.parent.postMessage(
              { type: "navigate", path: "/verification" },
              "*",
            );
          } catch (e) {
            // Ignore postMessage errors, navigation already happened
          }
        }
        return; // Exit early to prevent showing form fields
      } else {
        // Email doesn't exist or status is unknown - show registration form
        trackFreeTrialSignupStep("email_entry", 1);
        setEmailChecked(true);
        setAccountExists(false);
        setEmailError("An error occurred while checking the email");
      }
    } catch (error) {
      setEmailError("An error occurred while checking the email");
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleParentNameChange = (e) => {
    setParentName(e.target.value);
  };

  const handleChildYearGroupChange = (e) => {
    setChildYearGroup(e.target.value);
  };

  const handleRegister = async (e) => {
    e?.preventDefault();

    if (!parentName.trim()) {
      toast.error("Please enter parent name");
      return;
    }

    if (!childYearGroup) {
      toast.error("Please select child year group");
      return;
    }

    setRegistering(true);

    // Convert year group from "Year 3", "Year 4", "Year 5" or "year5", "year4" format to just "3", "4", or "5"
    const convertYearGroup = (yearGroup) => {
      if (!yearGroup) return yearGroup;
      // If it's already a number, return it as is
      if (/^\d+$/.test(yearGroup)) {
        return yearGroup;
      }
      // Extract number from "Year 3", "Year 4", "Year 5" (with space) or "year5", "year4" (no space)
      // Try with space first (e.g., "Year 3")
      let match = yearGroup.match(/year\s*(\d+)/i);
      if (match) {
        return match[1];
      }
      // Fallback: try to extract any number from the string
      match = yearGroup.match(/(\d+)/);
      return match ? match[1] : yearGroup;
    };

    const yearGroupNumber = convertYearGroup(childYearGroup);

    try {
      const response = await fetch(
        `${prefix}/api-40/api40/Registration/InsertFreeDownloadSignUp`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-API-Key": "8dnd6%3258%&k+_FsaTK25SCBNE58*t9",
          },
          body: JSON.stringify({
            email: email,
            name: parentName,
            year: yearGroupNumber,
            ip: ipAddress,
            marketingConsent: marketingConsent ? "Y" : "N",
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to register");
      }

      const data = await response.json();
      trackFreeTrialSignupStep("initial_details", 2);
      toast.success("Registration successful!");

      try {
        const recheckResponse = await fetch(
          `${prefix}/api-40/api40/Registration/CheckEmailStatus?Email=${encodeURIComponent(email.trim())}`,
          {
            headers: {
              "X-API-Key": "8dnd6%3258%&k+_FsaTK25SCBNE58*t9",
            },
          },
        );
        if (!recheckResponse.ok) {
          throw new Error("Failed to recheck email");
        }
        const recheckData = await recheckResponse.json();

        // Get status with fallback for case sensitivity
        const recheckStatus =
          getProperty(recheckData[0] || recheckData, "status") ||
          recheckData[0]?.Status ||
          recheckData[0]?.status ||
          recheckData?.status ||
          recheckData?.Status;

        if (
          recheckStatus === "FDL_Verified" ||
          recheckStatus === "FDL_Verified"
        ) {
          // User is already verified - continue to parent registration
          setFDLVerified(true);
          trackFreeTrialSignupStep("email_verification", 3);
          // Store email in localStorage for full account signup later
          localStorage.setItem("email", email.trim());
          sessionStorage.setItem("pendingLoginEmail", email.trim());
          navigate("/parentRegistration");
          // Also notify parent if in iframe
          if (window.parent !== window.self) {
            try {
              window.parent.postMessage(
                { type: "navigate", path: "/parentRegistration" },
                "*",
              );
            } catch (e) {
              // Ignore postMessage errors
            }
          }
          return; // Exit early to prevent further execution
        } else if (
          recheckStatus === "FDL_Not_Verified" ||
          recheckStatus === "FDL_Not_Verified"
        ) {
          // User needs verification - send email and navigate to verification screen
          setFDLNotVerified(true);
          sessionStorage.setItem("pendingLoginEmail", email.trim());
          sessionStorage.setItem("isFreeDownloadsRegistration", "true");
          // Also store in localStorage for persistence (for full account signup later)
          localStorage.setItem("email", email.trim());

          // Send verification email
          try {
            const verifyResponse = await fetch(
              `${prefix}/api-40/api40/Registration/EmailVerifcationCode?Email=${encodeURIComponent(email.trim())}`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  "X-API-Key": "8dnd6%3258%&k+_FsaTK25SCBNE58*t9",
                },
              },
            );
            if (!verifyResponse.ok) {
            }
          } catch (verifyError) {}

          // Navigate to verification screen
          navigate("/verification");
          // Also notify parent if in iframe
          if (window.parent !== window.self) {
            try {
              window.parent.postMessage(
                { type: "navigate", path: "/verification" },
                "*",
              );
            } catch (e) {
              // Ignore postMessage errors
            }
          }
          return; // Exit early to prevent further execution
        } else {
          // Unexpected status - show error
          setAccountExists(false);
          setEmailError("An error occurred while checking the email");
        }
      } catch (error) {
        // If recheck fails, default to verification flow
        sessionStorage.setItem("pendingLoginEmail", email.trim());
        sessionStorage.setItem("isFreeDownloadsRegistration", "true");
        navigate("/verification");
      }
    } catch (error) {
      toast.error("Failed to register");
      setRegistering(false);
    }
  };

  // Scroll to top when email is checked so user can fill in the form
  useEffect(() => {
    if (emailChecked && !emailExists) {
      // Scroll to the hero form when email is checked
      setTimeout(() => {
        scrollToForm();
      }, 100);
    }
  }, [emailChecked, emailExists]);

  // Handle scroll to show/hide sticky form
  useEffect(() => {
    const handleScroll = () => {
      if (heroFormRef.current) {
        const heroFormBottom =
          heroFormRef.current.getBoundingClientRect().bottom;
        // Show sticky form when scrolled past hero, and hide if email is checked (user will be navigated away)
        setShowStickyForm(heroFormBottom < 0 && !emailChecked);
      }
    };

    // Use Intersection Observer for better iframe compatibility
    let observer = null;
    if (heroFormRef.current && "IntersectionObserver" in window) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // If hero form is not intersecting (scrolled past), show sticky form
            setShowStickyForm(!entry.isIntersecting && !emailChecked);
          });
        },
        {
          root: null,
          rootMargin: "0px",
          threshold: 0,
        },
      );
      observer.observe(heroFormRef.current);
    }

    // Also listen to scroll events as fallback
    const scrollElements = [];

    // Add window scroll listener
    if (window) {
      scrollElements.push({ element: window, event: "scroll" });
    }

    // Add document scroll listeners (for iframe scenarios)
    if (document) {
      scrollElements.push({ element: document, event: "scroll" });
      if (document.documentElement) {
        scrollElements.push({
          element: document.documentElement,
          event: "scroll",
        });
      }
      if (document.body) {
        scrollElements.push({ element: document.body, event: "scroll" });
      }
    }

    // Add all scroll listeners
    scrollElements.forEach(({ element, event }) => {
      if (element && element.addEventListener) {
        element.addEventListener(event, handleScroll, { passive: true });
      }
    });

    handleScroll(); // Check on mount

    // Also check on resize for iframe scenarios
    const handleResize = () => {
      handleScroll();
    };
    window.addEventListener("resize", handleResize, { passive: true });

    // Periodic check for iframe scenarios where scroll events might not fire reliably
    const scrollCheckInterval = setInterval(() => {
      handleScroll();
    }, 300);

    return () => {
      if (observer && heroFormRef.current) {
        observer.unobserve(heroFormRef.current);
        observer.disconnect();
      }
      scrollElements.forEach(({ element, event }) => {
        if (element && element.removeEventListener) {
          element.removeEventListener(event, handleScroll);
        }
      });
      window.removeEventListener("resize", handleResize);
      clearInterval(scrollCheckInterval);
    };
  }, [emailChecked]);

  // Scroll to form function
  const scrollToForm = () => {
    if (heroFormRef.current) {
      heroFormRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const renderTrialExistsMessage = () => (
    <div className="text-sm text-amber-700 mt-2">
      <p>
        {existingAccountMessage ||
          "Please log in with your KSOL username and password, or call us on 0121 733 6558."}
      </p>
      <button
        type="button"
        onClick={openMembersLoginInNewWindow}
        className="underline font-semibold mt-2"
      >
        Open Members Login
      </button>
    </div>
  );

  useEffect(() => {
    const scrollHeroFormIntoView = () => {
      if (heroFormRef.current) {
        heroFormRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    };

    const handleMessage = (event) => {
      const messageType = event?.data?.type;
      const messagePath = event?.data?.path;

      if (messageType === "scrollToForm") {
        scrollHeroFormIntoView();
      }

      if (
        messageType === "navigate" &&
        (messagePath === "#free-trial-form" ||
          messagePath === "/#free-trial-form")
      ) {
        scrollHeroFormIntoView();
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  // Render form component (reusable)
  const renderForm = (isSticky = false) => {
    return (
      <div
        className={
          isSticky
            ? "bg-white rounded-lg p-2 sm:p-3  border-gray-200"
            : "bg-white drop-shadow-xl rounded-2xl sm:rounded-3xl p-4 sm:p-6 xl:p-8 border-2 sm:border-4"
        }
        style={
          !isSticky ? { borderColor: "#fd943d" } : { pointerEvents: "auto" }
        }
      >
        {!isSticky && (
          <h2
            className="text-2xl sm:text-3xl xl:text-4xl font-bold mb-2"
            style={{ color: "#fd943d" }}
          >
            Start Free Trial
          </h2>
        )}
        {!isSticky && (
          <p className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6 font-medium">
            No card details required
          </p>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!emailChecked && email.trim()) {
              checkEmailExists();
            }
          }}
          className="w-full"
          style={{ pointerEvents: "auto" }}
        >
          {!emailChecked && (
            <div className="w-full">
              {!isSticky && (
                <p className="mb-2 text-gray-700 text-sm sm:text-base">
                  Email <span style={{ color: "red" }}>*</span>
                </p>
              )}
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex-1 email-input-wrapper">
                  <CustomInput
                    type="email"
                    inputType="email"
                    placeholder={isSticky ? "Enter email" : "Enter your email"}
                    value={email}
                    onChange={handleEmailChange}
                    autoComplete="email"
                    errorMessage={emailError}
                  />
                </div>
                <div className="sm:flex-shrink-0 email-button-wrapper w-full sm:w-auto">
                  <CustomButton
                    onClick={checkEmailExists}
                    text={checkingEmail ? "Checking..." : "Start Trial"}
                  />
                </div>
              </div>
              {!isSticky && (
                <p className="text-xs text-gray-500 mt-2">
                  If you already have an account, we'll prompt for your
                  password. Otherwise, you can register here.
                </p>
              )}
              {trialExistsMessage && renderTrialExistsMessage()}
            </div>
          )}

          {emailChecked && !emailExists && (
            <div className="space-y-4">
              <div className="w-full">
                <p className="mb-2 text-gray-700 text-xs sm:text-sm">
                  Email <span style={{ color: "red" }}>*</span>
                </p>
                <div className="p-2 sm:p-4 w-full border-2 border-gray-200 rounded-lg bg-gray-50">
                  <p className="text-gray-700 font-medium text-xs sm:text-sm">
                    {email}
                  </p>
                </div>
              </div>
              <div className="w-full">
                <p className="mb-2 text-gray-700 text-xs sm:text-sm">
                  Parent Name <span style={{ color: "red" }}>*</span>
                </p>
                <CustomInput
                  type="text"
                  inputType="text"
                  placeholder="Enter your name"
                  value={parentName}
                  onChange={handleParentNameChange}
                  autoComplete="name"
                />
              </div>
              <div className="w-full">
                <p className="mb-2 text-gray-700 text-xs sm:text-sm">
                  Child Year Group (Year 3, 4 or 5){" "}
                  <span style={{ color: "red" }}>*</span>
                </p>
                <CustomInput
                  type="year"
                  inputType="year"
                  placeholder="Select year group (3, 4 or 5)"
                  value={childYearGroup}
                  onChange={handleChildYearGroupChange}
                  isYearDropdown={true}
                />
              </div>
              <div className="w-full">
                <CustomCheckbox
                  label="I'd like to receive updates about KSOL and our products"
                  checked={marketingConsent}
                  onChange={handleMarketingConsentChange}
                />
              </div>
              <div className="w-full pt-2">
                <CustomButton
                  onClick={handleRegister}
                  text={registering ? "Registering..." : "Start Free Trial"}
                  disabled={registering}
                />
              </div>
            </div>
          )}
        </form>
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>11+ Free Week Trial | KSOL</title>
        <meta
          name="description"
          content="Book your child's free 11+ week trial with KSOL for Year 3, Year 4 or Year 5. Includes guided online tests, support resources, adviser follow-up, and a sample progress report."
        />
        <meta
          name="keywords"
          content="11+ free trial, 11+ week trial, 11+ preparation, year 3 11+, year 4 11+, year 5 11+, verbal reasoning, non-verbal reasoning, KSOL"
        />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="11+ Free Week Trial | KSOL" />
        <meta
          property="og:description"
          content="A structured free 11+ week trial for Year 3, Year 4 and Year 5 families with clear support and next-step guidance."
        />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="11+ Free Week Trial | KSOL" />
        <meta
          name="twitter:description"
          content="Book a free 11+ week trial and get a structured start with clear support for Year 3, Year 4 and Year 5 preparation."
        />
        {/* Note: X-Frame-Options must be set via HTTP headers on the server, not meta tags */}
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes"
        />
      </Helmet>
      {/* Full-page loading overlay/lightbox */}
      {registering && (
        <UniformLoader
          title="Registering your email..."
          message="Please wait while we register your email"
          subMessage="This may take a few moments"
          fullPage={true}
        />
      )}
      <div className="p-4 sm:p-6 md:p-8 lg:p-10 flex flex-col items-center justify-start free-week-trial-page-bg relative">
        <div className="flex flex-col w-full items-center xl:w-3/4">
          {/* Hero Section with Form Above the Fold */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 xl:gap-12 mb-8 sm:mb-12">
            {/* Left Column: Value Proposition */}
            <div className="flex flex-col justify-center">
              <div className="bg-white drop-shadow-lg rounded-2xl sm:rounded-3xl p-4 sm:p-6 xl:p-8">
                <h1 className="text-2xl sm:text-3xl xl:text-4xl font-bold mb-3 sm:mb-4 text-gray-900">
                  11 Plus Free Week Trial
                </h1>
                <h2
                  className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4"
                  style={{ color: "#fd943d" }}
                >
                  Take the first step to 11+ success!
                </h2>
                <p className=" text-base text-gray-700 mb-4 sm:mb-6">
                  Each trial is tailored to your child's age group, giving them
                  a personalised experience at the right level. During the
                  trial, your child will:
                </p>

                {/* Quick Benefits List */}
                <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                  <div className="flex items-start">
                    <FontAwesomeIcon
                      icon={faCheckCircle}
                      className="mr-2 sm:mr-3 mt-1 flex-shrink-0"
                      style={{ color: "#9fd066", fontSize: "20px" }}
                    />
                    <p className="text-sm sm:text-base text-gray-700">
                      Build confidence across key 11+ subjects
                    </p>
                  </div>
                  <div className="flex items-start">
                    <FontAwesomeIcon
                      icon={faCheckCircle}
                      className="mr-2 sm:mr-3 mt-1 flex-shrink-0"
                      style={{ color: "#9fd066", fontSize: "20px" }}
                    />
                    <p className="text-sm sm:text-base text-gray-700">
                      Engage with structured, interactive lessons and video
                      learning
                    </p>
                  </div>
                  <div className="flex items-start">
                    <FontAwesomeIcon
                      icon={faCheckCircle}
                      className="mr-2 sm:mr-3 mt-1 flex-shrink-0"
                      style={{ color: "#9fd066", fontSize: "20px" }}
                    />
                    <p className="text-sm sm:text-base text-gray-700">
                      Begin to recognise their strengths
                    </p>
                  </div>
                </div>

                <p className="text-base  text-gray-700 mb-4 sm:mb-6">
                  Afterwards, you'll receive a personalised progress report from
                  their allocated tutor, plus a call from our Education Team to
                  talk through the experience.
                </p>

                <p className="text-base text-gray-700 mb-4 sm:mb-6 font-bold">
                  Register today and take the first step towards confident 11+
                  preparation.
                </p>

                <div className="border-t pt-4">
                  <p className="text-sm text-gray-600 mb-2">
                    <span className="font-semibold">
                      No card details required
                    </span>{" "}
                    • Try KSOL&apos;s award-winning 11+ course
                  </p>
                  <p className="text-sm text-gray-500">
                    Helping every child get the best possible start to secondary
                    education
                  </p>
                </div>
              </div>
            </div>

            {/* Right Column: Registration Form */}
            <div className="flex flex-col justify-center" ref={heroFormRef}>
              <div
                className="bg-white drop-shadow-xl rounded-2xl sm:rounded-3xl p-4 sm:p-6 xl:p-8 border-2 sm:border-4"
                style={{ borderColor: "#fd943d" }}
              >
                <h2
                  className="text-2xl sm:text-3xl xl:text-4xl font-bold mb-2"
                  style={{ color: "#fd943d" }}
                >
                  Start Free Trial
                </h2>
                <p className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6 font-medium">
                  No card details required
                </p>

                <form onSubmit={(e) => e.preventDefault()} className="w-full">
                  {!emailChecked && (
                    <div className="w-full">
                      <p className="mb-2 text-gray-700">
                        Email <span style={{ color: "red" }}>*</span>
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                        <div className="flex-1 email-input-wrapper">
                          <CustomInput
                            type="email"
                            inputType="email"
                            placeholder="Enter your email"
                            value={email}
                            onChange={handleEmailChange}
                            autoComplete="email"
                            errorMessage={emailError}
                          />
                        </div>
                        <div className="sm:flex-shrink-0 email-button-wrapper w-full sm:w-auto">
                          <CustomButton
                            onClick={checkEmailExists}
                            text={checkingEmail ? "Checking..." : "Start Trial"}
                          />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-6">
                        Enter your email so we can check your access and show
                        the right next step. <br />
                        If you already have a KSOL account, you will be asked to
                        log in with your username and password.
                      </p>
                      {trialExistsMessage && renderTrialExistsMessage()}
                    </div>
                  )}

                  {emailChecked && !emailExists && (
                    <div className="space-y-4">
                      <div className="w-full">
                        <p className="mb-2 text-gray-700">
                          Email <span style={{ color: "red" }}>*</span>
                        </p>
                        <div className="p-4 w-full border-2 border-gray-200 rounded-lg bg-gray-50">
                          <p className="text-gray-700 font-medium">{email}</p>
                        </div>
                      </div>
                      <div className="w-full">
                        <p className="mb-2 text-gray-700">
                          Parent Name <span style={{ color: "red" }}>*</span>
                        </p>
                        <CustomInput
                          type="text"
                          inputType="text"
                          placeholder="Enter your name"
                          value={parentName}
                          onChange={handleParentNameChange}
                          autoComplete="name"
                        />
                      </div>
                      <div className="w-full">
                        <p className="mb-2 text-gray-700">
                          Child Year Group (Year 3, 4 or 5){" "}
                          <span style={{ color: "red" }}>*</span>
                        </p>
                        <CustomInput
                          type="year"
                          inputType="year"
                          placeholder="Select year group (Year 3, 4 or 5)"
                          value={childYearGroup}
                          onChange={handleChildYearGroupChange}
                          isYearDropdown={true}
                        />
                      </div>
                      <div className="w-full">
                        <CustomCheckbox
                          label="I'd like to receive updates about KSOL and our products"
                          checked={marketingConsent}
                          onChange={handleMarketingConsentChange}
                        />
                      </div>
                      <div className="w-full pt-2">
                        <CustomButton
                          onClick={handleRegister}
                          text={
                            registering ? "Registering..." : "Start Free Trial"
                          }
                          disabled={registering}
                        />
                      </div>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Sticky Form (appears after scrolling past hero) - Subtle and minimal */}
        {showStickyForm && (
          <div
            className="fixed bottom-0 left-0 right-0 p-2 sm:p-3 bg-white border-t border-gray-200 shadow-lg sticky-form-enter"
            style={{
              zIndex: 9999, // High z-index for iframe compatibility
              position: "fixed",
              pointerEvents: "auto", // Ensure it's clickable
            }}
          >
            <div
              className="max-w-4xl mx-auto px-2 sm:px-4"
              style={{ pointerEvents: "auto" }}
            >
              {renderForm(true)}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default FreeDownloadsRegister;
